// ============================================================================
// Canonical amenity taxonomy + synonym map (SHARED — keep byte-identical across
// VT-Front, VTHub, and SHub).
//
// WHY: every PMS emits amenities in its OWN raw vocabulary — Guesty strings,
// Hostaway names, BookingPal categories, RU switcher names / numeric ids, SHub
// codes. The FE offers ~25 hand-curated marketing labels ("Beach Front", "Pool",
// "Parking Space") that match almost none of those raw strings, so amenity search
// never "reflected" the picks. This module maps the raw variants onto a small set
// of canonical facet slugs, so matching + ranking are accurate and identical
// everywhere.
//
// HOW a raw amenity matches a facet:
//   1. EXACT   — its normalized (or space-squashed) form is in `exact`.
//   2. INCLUDE — it contains one of `includes` AND none of `excludes`
//                (guards e.g. "pool table" / "theme park" from matching pool/park).
// EXACT is precise (curated from the live data); INCLUDE adds recall for unseen
// variants. Grow the lists as new raw strings surface — never loosen a guard.
// ============================================================================

// lowercase, punctuation -> space, collapse. "Beach Front" -> "beach front".
export const normalizeAmenity = (s) =>
  String(s == null ? '' : s).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
// space-squashed form so "beach front" and "Beachfront" unify. -> "beachfront".
const squash = (s) => normalizeAmenity(s).replace(/\s+/g, '');

// slug -> { label, exact:[normalized variants], includes:[tokens], excludes:[tokens] }
export const FACETS = {
  pool: {
    label: 'Pool',
    exact: ['pool', 'swimming pool', 'outdoor pool', 'private pool', 'communal pool',
      'community pool', 'heated pool', 'heated outdoor pool', 'heated private pool',
      'infinity pool', 'plunge pool', 'rooftop pool', 'indoor pool', 'fenced pool',
      'kids pool', 'childrens pool', 'shallow end pool'],
    includes: ['pool'],
    // NOT a pool: games table, poolside furniture/extras, "pool view" (a view), whirlpool.
    excludes: ['pool table', 'billiard', 'pool bar', 'pool towel', 'pool umbrella',
      'pool sun lounger', 'pool lounger', 'pool view', 'pool with view', 'whirpool', 'whirlpool'],
  },
  private_beach: {
    label: 'Private Beach',
    exact: ['private beach', 'near private beach direct access'],
    includes: ['private beach'], excludes: [],
  },
  beachfront: {
    label: 'Beach Front',
    exact: ['beach front', 'beachfront', 'location beachfront'],
    includes: ['beachfront', 'beach front'], excludes: [],
  },
  beach_access: {
    label: 'Beach Access',
    exact: ['beach', 'beach access', 'sea access', 'sea', 'ocean', 'near ocean',
      'ocean front', 'location ocean front', 'waterfront', 'water view', 'water front'],
    includes: ['beach access', 'sea access'],
    excludes: ['beach towel', 'beach essential', 'beach view'],
  },
  sea_view: {
    label: 'Sea View',
    exact: ['sea view', 'seaview', 'partial sea view', 'ocean view', 'location ocean view',
      'bay view', 'water view', 'beach view'],
    includes: ['sea view', 'ocean view'], excludes: [],
  },
  mountain_view: {
    label: 'Mountain Views',
    exact: ['mountain view', 'mountain views'],
    includes: ['mountain view'], excludes: [],
  },
  parking: {
    label: 'Parking Space',
    exact: ['parking', 'parking space', 'free parking', 'free parking on premises',
      'free parking on street', 'free parking on the street', 'free street parking',
      'private parking', 'outdoor parking', 'underground parking', 'on street parking',
      'secured parking space', 'garage parking', 'free parking with garage',
      'driveway parking', 'guarded parking', 'paid parking', 'paid parking on premises',
      'paid parking off premises', 'disabled parking spot'],
    includes: ['parking'],
    excludes: ['theme park', 'water park', 'water parks', 'parking reservation required'],
  },
  garage: {
    label: 'Garage',
    exact: ['garage', 'garage parking', 'free parking with garage'],
    includes: ['garage'], excludes: [],
  },
  garden: {
    label: 'Garden',
    exact: ['garden'],
    includes: [], excludes: [], // exact only — avoid "garden view" (a view)
  },
  gym: {
    label: 'Home Gym',
    exact: ['gym', 'home gym', 'fitness', 'fitness room', 'fitness center', 'fitness centre'],
    includes: ['gym', 'fitness'], excludes: [],
  },
  hot_tub: {
    label: 'Jacuzzi',
    exact: ['jacuzzi', 'jaccuzi', 'hot tub', 'indoor jacuzzi', 'outdoor jacuzzi',
      'whirpool', 'whirlpool', 'spa bath'],
    includes: ['jacuzzi', 'hot tub'], excludes: [],
  },
  sauna: {
    label: 'Sauna',
    exact: ['sauna', 'sauna steam room'],
    includes: ['sauna'], excludes: [],
  },
  steam_room: {
    label: 'Steam Room',
    exact: ['steam room', 'sauna steam room'],
    includes: ['steam room'], excludes: [],
  },
  spa: {
    label: 'Spa',
    exact: ['spa', 'spa bath'],
    includes: [], excludes: [], // exact only — "spa" is broad; avoid loose substring
  },
  outdoor_kitchen: {
    label: 'Summer Kitchen',
    exact: ['summer kitchen', 'outdoor kitchen'],
    includes: ['outdoor kitchen'], excludes: [],
  },
  terrace: {
    label: 'Terrace',
    exact: ['terrace', 'balcony', 'patio', 'roof terrace'],
    includes: ['terrace', 'balcony'], excludes: [],
  },
  home_theater: {
    label: 'Theater Room',
    exact: ['theater room', 'theatre room', 'home theater', 'home theatre',
      'home cinema', 'cinema room', 'media room'],
    includes: ['home theater', 'home cinema', 'cinema room'], excludes: [],
  },
  wine: {
    label: 'Wine Bar',
    exact: ['wine bar', 'wine cellar', 'wine fridge', 'wine cooler'],
    includes: ['wine cellar', 'wine bar'], excludes: [],
  },
  ski_in_out: {
    label: 'Ski In/Out',
    exact: ['ski in and out', 'ski in ski out', 'ski in out', 'ski in', 'ski out'],
    includes: ['ski in', 'ski out', 'ski in ski out'], excludes: [],
  },
  skiing: {
    label: 'Skiing',
    exact: ['skiing', 'ski room', 'ski storage', 'ski in and out', 'ski in ski out'],
    includes: ['skiing', 'ski room', 'ski storage'], excludes: ['water skiing'],
  },
  helipad: {
    label: 'Helipad',
    exact: ['helipad', 'helicopter pad', 'helicopter landing'],
    includes: ['helipad'], excludes: [],
  },
  large_plot: {
    label: 'Large Plot',
    exact: ['large plot', 'large grounds', 'large garden', 'extensive grounds'],
    includes: ['large plot'], excludes: [],
  },
  natural_surroundings: {
    label: 'Natural surroundings',
    exact: ['natural surroundings', 'nature', 'countryside', 'country view',
      'country or rural view', 'rural'],
    includes: ['natural surrounding'], excludes: [],
  },
  near_marina: {
    label: 'Near Marina',
    exact: ['marina', 'near marina'],
    includes: ['marina'], excludes: [],
  },
  country_club: {
    label: 'Nearby Country Club',
    exact: ['country club', 'nearby country club', 'golf course', 'golf course view', 'golf view'],
    includes: ['country club', 'golf course'], excludes: [],
  },
  shopping: {
    label: 'Nearby Shopping',
    exact: ['shopping', 'nearby shopping', 'shopping mall', 'shops nearby', 'shopping nearby'],
    includes: ['shopping'], excludes: [],
  },

  // ---- extra common facets (not FE-pickable yet, but enrich the match ranking) ----
  wifi: {
    label: 'WiFi',
    exact: ['wifi', 'wi fi', 'wireless internet', 'free wireless internet', 'internet',
      'internet connection', 'high speed internet access', 'free high speed wireless internet',
      'free internet access', 'pocket wifi', 'free cable internet'],
    includes: ['wifi', 'wi fi'], excludes: ['wifi usb adapter'],
  },
  air_conditioning: {
    label: 'Air Conditioning',
    exact: ['air conditioning', 'air conditioning heating', 'ac', 'a c', 'aircon', 'climate control'],
    includes: ['air conditioning', 'air condition'], excludes: [],
  },
  heating: {
    label: 'Heating',
    exact: ['heating', 'central heating', 'underfloor heating', 'air conditioning heating'],
    includes: ['central heating', 'underfloor heating'], excludes: [],
  },
  fireplace: {
    label: 'Fireplace',
    exact: ['fireplace', 'indoor fireplace', 'wood burning fireplace'],
    includes: ['fireplace'], excludes: ['fireplace guard'],
  },
  tv: {
    label: 'TV',
    exact: ['tv', 'cable tv', 'smart tv', 'satellite tv', 'apple tv', 'tv satellite'],
    includes: [], excludes: [], // exact only — avoid CCTV / "internet browser tv"
  },
  kitchen: {
    label: 'Kitchen',
    exact: ['kitchen', 'full kitchen', 'fully equipped kitchen', 'fully fitted kitchen',
      'kitchenette', 'chefs kitchen', 'professional kitchen'],
    includes: [], excludes: [],
  },
  washer: {
    label: 'Washer',
    exact: ['washer', 'washing machine', 'washer dryer', 'washer in common space'],
    includes: ['washing machine'], excludes: [],
  },
  dishwasher: {
    label: 'Dishwasher',
    exact: ['dishwasher'], includes: ['dishwasher'], excludes: ['start the dishwasher'],
  },
  pet_friendly: {
    label: 'Pet Friendly',
    exact: ['pets allowed', 'pets are welcome', 'pets accepted under request', 'pets paid',
      'pets live on this property', 'pet friendly'],
    includes: ['pets allowed', 'pet friendly'], excludes: ['no pets'],
  },
  elevator: {
    label: 'Elevator',
    exact: ['elevator', 'lift'], includes: ['elevator'], excludes: [],
  },
  wheelchair_accessible: {
    label: 'Wheelchair Accessible',
    exact: ['wheelchair accessible', 'wheelchair access possible', 'wheelchair friendly',
      'accessible path of travel', 'no stairs or steps to enter'],
    includes: ['wheelchair'], excludes: [],
  },
  bbq: {
    label: 'BBQ',
    exact: ['bbq', 'barbecue', 'barbeque', 'bbq grill', 'outdoor grill'],
    includes: ['bbq', 'barbecue', 'barbeque'], excludes: [],
  },
};

// Map a FE filter value/label (what the picker currently sends) to a facet slug.
// Built from every facet label + the FE's exact picker strings (incl. their typos).
const ALIASES = {};
for (const [slug, f] of Object.entries(FACETS)) {
  ALIASES[normalizeAmenity(slug)] = slug;
  ALIASES[normalizeAmenity(f.label)] = slug;
}
// FE picker labels whose spelling/wording differs from the facet label:
Object.assign(ALIASES, {
  [normalizeAmenity('Jaccuzi')]: 'hot_tub',
  [normalizeAmenity('Outdoor Pool')]: 'pool',
  [normalizeAmenity('Mountain Views')]: 'mountain_view',
  [normalizeAmenity('Natuaral surroundings')]: 'natural_surroundings',
  [normalizeAmenity('Ski In/Out')]: 'ski_in_out',
});

// Resolve a picked filter value (slug OR marketing label) to a canonical slug, or null.
export const slugForFilterValue = (value) => {
  const n = normalizeAmenity(value);
  if (!n) return null;
  if (FACETS[value]) return value;         // already a slug
  return ALIASES[n] || null;
};

// Does one RAW amenity string satisfy a facet?
const rawAmenityMatchesFacet = (rawAmenity, facet) => {
  const n = normalizeAmenity(rawAmenity);
  if (!n) return false;
  const sq = n.replace(/\s+/g, '');
  for (const e of facet.exact) {
    if (n === e || sq === squash(e)) return true;
  }
  if (facet.includes.length) {
    if (facet.excludes.some((x) => n.includes(x))) return false;
    if (facet.includes.some((t) => n.includes(t))) return true;
  }
  return false;
};

// Which facet slugs does a raw amenity satisfy? (used by the sync-time normalizer)
export const facetsForAmenity = (rawAmenity) => {
  const out = [];
  for (const [slug, f] of Object.entries(FACETS)) {
    if (rawAmenityMatchesFacet(rawAmenity, f)) out.push(slug);
  }
  return out;
};

// Normalize a listing's raw amenities array to the set of canonical facet slugs it has.
export const normalizeAmenitiesList = (rawAmenities) => {
  const set = new Set();
  for (const a of (Array.isArray(rawAmenities) ? rawAmenities : [])) {
    for (const slug of facetsForAmenity(a)) set.add(slug);
  }
  return [...set];
};

// Build anchored, whitespace/punctuation-flexible regexes for one picked filter
// value's facet — for MATCH-TIME expansion in the hubs' addFilterToQuery, so a
// picked "Beach Front" matches stored "Beachfront" / "beach-front". Anchored (^…$)
// to the whole amenity element so "Pool" never matches "Pool table" (that precision
// is why we expand the curated `exact` variants rather than a loose substring).
// Unknown values fall back to a single coarse substring regex (the hubs' prior
// behavior), so nothing regresses. Used by VTHub + SHub, not the FE.
export const regexesForFilterValue = (value) => {
  const flexible = (variant) => {
    const esc = String(variant).replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '[\\s\\W_]*');
    return new RegExp('^' + esc + '$', 'i');
  };
  const slug = slugForFilterValue(value);
  if (slug && FACETS[slug]) return FACETS[slug].exact.map(flexible);
  return [new RegExp(String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i')];
};

// Build a predicate for one picked filter value: true if a listing's amenities
// satisfy that value's facet. Falls back to flexible substring matching on the raw
// value when the value maps to no known facet (nothing regresses for unknown picks).
export const matcherForFilterValue = (value) => {
  const slug = slugForFilterValue(value);
  if (slug && FACETS[slug]) {
    const facet = FACETS[slug];
    return {
      value, slug,
      test: (amenities) =>
        (Array.isArray(amenities) ? amenities : []).some((a) => rawAmenityMatchesFacet(a, facet)),
    };
  }
  // Unknown value → flexible fallback (case-insensitive, space≈hyphen≈nothing).
  const rx = new RegExp(
    normalizeAmenity(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '[\\s-]*') || '\\b\\B',
    'i'
  );
  return {
    value, slug: null,
    test: (amenities) => rx.test((Array.isArray(amenities) ? amenities : []).join(' | ')),
  };
};
